import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, Trash, Plus } from "@phosphor-icons/react";
import { localDateStr } from "@/lib/date";

export default function Reminders() {
  const [items, setItems] = useState([]);
  // Split the datetime into two independent controls so mobile pickers (esp. iOS
  // Safari) stop showing a compound wheel where the columns don't line up with
  // the user's expectation of hours vs. minutes.
  const [form, setForm] = useState({ title: "", date: localDateStr(), time: "09:00", recurrence: "none" });

  const load = useCallback(async () => {
    const { data } = await api.get("/reminders");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  }, [load]);

  useEffect(() => {
    if (!("Notification" in window)) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/reminders");
        const now = new Date();
        data.forEach(r => {
          const t = new Date(r.remind_at);
          if (r.active && Math.abs(t - now) < 60000 && Notification.permission === "granted") {
            new Notification("AL-AXE Reminder", { body: r.title });
          }
        });
      } catch { /* polling loop — a single missed poll is fine */ }
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.time) return;
    // Combine to a local ISO string like "2026-02-10T09:15:00" — the backend
    // stores it as-is and every timeline / notification treats it in local tz.
    const remind_at = `${form.date}T${form.time}`;
    await api.post("/reminders", { title: form.title, remind_at, recurrence: form.recurrence });
    setForm({ title: "", date: localDateStr(), time: "09:00", recurrence: "none" });
    toast.success("Reminder set");
    load();
  };
  const del = async (id) => { await api.delete(`/reminders/${id}`); load(); };

  const inp = "rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400";
  return (
    <div data-testid="reminders-page" className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Reminders</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Nudges that stick</h1>
      </header>
      <form onSubmit={add} className="bg-white border border-slate-200/70 rounded-2xl p-5 grid md:grid-cols-6 gap-3">
        <input data-testid="rem-title" required placeholder="Title" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className={`${inp} md:col-span-3`}/>
        <div className="md:col-span-1">
          <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Date</label>
          <input data-testid="rem-date" required type="date" value={form.date} onChange={e=>setForm({...form, date: e.target.value})} className={`${inp} w-full`}/>
        </div>
        <div className="md:col-span-1">
          <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Time</label>
          <input data-testid="rem-time" required type="time" step="60" value={form.time} onChange={e=>setForm({...form, time: e.target.value})} className={`${inp} w-full`}/>
        </div>
        <div className="md:col-span-1">
          <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Repeats</label>
          <select data-testid="rem-recurrence" value={form.recurrence} onChange={e=>setForm({...form, recurrence: e.target.value})} className={`${inp} w-full`}>
            <option value="none">Once</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <button data-testid="rem-add" className="al-gradient text-white rounded-full py-2 font-medium md:col-span-6 md:w-auto md:px-6 md:justify-self-end"><Plus size={14} className="inline mr-1"/>Add reminder</button>
      </form>
      <div className="bg-white border border-slate-200/70 rounded-2xl divide-y divide-slate-100">
        {items.length === 0 && <div className="p-6 text-slate-400 text-sm">No reminders yet.</div>}
        {items.map(r => (
          <div key={r.id} className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full al-gradient text-white flex items-center justify-center"><Bell size={16} weight="fill"/></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{r.title}</div>
              <div className="text-xs text-slate-500">{new Date(r.remind_at).toLocaleString()} · {r.recurrence}</div>
            </div>
            <button data-testid={`rem-del-${r.id}`} onClick={()=>del(r.id)} className="text-slate-400 hover:text-red-500"><Trash size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
